import { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import {
  Sparkles, Zap, Image, Video, Mic, Send, BarChart3, Users, Calendar,
  ChevronRight, Check, Play, ArrowRight, Star, Globe, Shield, Clock,
  Layers, Palette, Brain, Target, TrendingUp, Award, ChevronDown, Menu, X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* ═══════════════════════ ANIM HELPERS ═══════════════════════ */

const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.25, 0.4, 0.25, 1] } } };
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };
const scaleIn = { hidden: { opacity: 0, scale: 0.9 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } } };

function Section({ children, className = '', id }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section ref={ref} id={id} initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={stagger}
      className={`relative px-4 sm:px-6 lg:px-8 ${className}`}>
      {children}
    </motion.section>
  );
}

/* ═══════════════════════ NAV ═══════════════════════ */

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  const links = [
    { label: 'Özellikler', href: '#features' },
    { label: 'Nasıl Çalışır', href: '#how' },
    { label: 'Fiyatlandırma', href: '#pricing' },
    { label: 'SSS', href: '#faq' },
  ];

  return (
    <motion.nav initial={{ y: -100 }} animate={{ y: 0 }} transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/5' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <a href="#hero" className="flex items-center gap-2" data-testid="nav-logo">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center">
            <Sparkles size={16} className="text-white" />
          </div>
          <span className="font-outfit text-xl font-bold text-white tracking-tight">Sosyal<span className="text-violet-400">.Team</span></span>
        </a>

        <div className="hidden md:flex items-center gap-8">
          {links.map(l => (
            <a key={l.href} href={l.href} className="text-sm text-[#A3A3A3] hover:text-white transition-colors font-ibm">{l.label}</a>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <button onClick={() => navigate('/login')} data-testid="nav-login-btn" className="text-sm text-[#A3A3A3] hover:text-white transition-colors font-ibm px-4 py-2">
            Giriş Yap
          </button>
          <button onClick={() => navigate('/login')} data-testid="nav-cta-btn"
            className="text-sm font-ibm font-medium px-5 py-2.5 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 text-white hover:from-violet-500 hover:to-blue-500 transition-all shadow-lg shadow-violet-500/20">
            Ücretsiz Başla
          </button>
        </div>

        <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden text-white" data-testid="mobile-menu-btn">
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileOpen && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="md:hidden bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/5 px-4 pb-4">
          {links.map(l => (
            <a key={l.href} href={l.href} onClick={() => setMobileOpen(false)} className="block py-3 text-sm text-[#A3A3A3] hover:text-white font-ibm">{l.label}</a>
          ))}
          <button onClick={() => navigate('/login')} className="w-full mt-3 text-sm font-ibm font-medium px-5 py-2.5 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 text-white">
            Ücretsiz Başla
          </button>
        </motion.div>
      )}
    </motion.nav>
  );
}

/* ═══════════════════════ HERO ═══════════════════════ */

function Hero({ content }) {
  const navigate = useNavigate();
  const c = content?.hero || {};
  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16" data-testid="hero-section">
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-violet-600/15 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-blue-600/10 rounded-full blur-[100px]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-violet-500/20 to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-violet-500/20 bg-violet-500/5 mb-8">
          <Sparkles size={14} className="text-violet-400" />
          <span className="text-xs font-ibm text-violet-300">{c.badge || 'Yapay Zeka Destekli İçerik Platformu'}</span>
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.8 }}
          className="font-outfit text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-[1.1] tracking-tight max-w-4xl mx-auto">
          {c.title_1 || 'İçerik '}
          <span className="bg-gradient-to-r from-violet-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
            {c.title_highlight || 'Oluştur, Otomatikleştir'}
          </span>
          {' '}{c.title_2 || 've Ölçeklendir'}
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          className="mt-6 text-base sm:text-lg text-[#A3A3A3] font-ibm max-w-2xl mx-auto leading-relaxed">
          {c.subtitle || 'Metin, görsel, video ve ses içerikleri tek bir platformda AI ile üretin. Sosyal medya hesaplarınıza otomatik yayınlayın.'}
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }}
          className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
          <button onClick={() => navigate('/login')} data-testid="hero-cta-btn"
            className="group inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 text-white font-ibm font-medium text-sm hover:from-violet-500 hover:to-blue-500 transition-all shadow-2xl shadow-violet-500/25 hover:shadow-violet-500/40 hover:scale-105">
            {c.cta_primary || 'Ücretsiz Başla'} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <a href="#how" data-testid="hero-demo-btn"
            className="group inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full border border-white/10 text-white font-ibm font-medium text-sm hover:bg-white/5 transition-all">
            <Play size={16} className="text-violet-400" /> {c.cta_secondary || 'Demo İzle'}
          </a>
        </motion.div>

        {/* Dashboard Mockup */}
        <motion.div initial={{ opacity: 0, y: 60, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ delay: 0.9, duration: 1 }}
          className="mt-16 md:mt-24 relative max-w-5xl mx-auto">
          <div className="absolute -inset-4 bg-gradient-to-b from-violet-600/20 via-transparent to-transparent rounded-2xl blur-2xl" />
          <div className="relative rounded-xl border border-white/10 bg-[#0F0F0F] overflow-hidden shadow-2xl">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-[#0A0A0A]">
              <div className="flex gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-[#FF5F56]" /><div className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E]" /><div className="w-2.5 h-2.5 rounded-full bg-[#27CA40]" /></div>
              <span className="text-[10px] text-[#525252] font-jetbrains ml-3">sosyal.team/dashboard</span>
            </div>
            <div className="p-6 grid grid-cols-12 gap-4 min-h-[300px]">
              <div className="col-span-2 hidden md:block space-y-3">
                {['Dashboard', 'AI Üret', 'Takvim', 'Analizler'].map((t, i) => (
                  <div key={t} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-ibm ${i === 1 ? 'bg-violet-500/10 text-violet-400' : 'text-[#525252]'}`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${i === 1 ? 'bg-violet-400' : 'bg-[#262626]'}`} />
                    {t}
                  </div>
                ))}
              </div>
              <div className="col-span-12 md:col-span-10 space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'İçerik Üretildi', val: '2,847', color: 'from-violet-500 to-violet-600' },
                    { label: 'Yayınlanan', val: '1,234', color: 'from-blue-500 to-blue-600' },
                    { label: 'Erişim', val: '45.2K', color: 'from-cyan-500 to-cyan-600' },
                    { label: 'Etkileşim', val: '%8.3', color: 'from-green-500 to-green-600' },
                  ].map(s => (
                    <div key={s.label} className="p-3 rounded-lg bg-[#141414] border border-white/5">
                      <p className="text-[10px] text-[#525252] font-ibm">{s.label}</p>
                      <p className={`text-lg font-outfit font-bold bg-gradient-to-r ${s.color} bg-clip-text text-transparent`}>{s.val}</p>
                    </div>
                  ))}
                </div>
                <div className="p-4 rounded-lg bg-[#141414] border border-white/5">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles size={14} className="text-violet-400" />
                    <span className="text-xs text-white font-ibm">AI İçerik Üretici</span>
                  </div>
                  <div className="h-2 w-3/4 bg-gradient-to-r from-violet-500/30 to-blue-500/30 rounded-full overflow-hidden">
                    <motion.div animate={{ width: ['0%', '100%'] }} transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                      className="h-full bg-gradient-to-r from-violet-500 to-blue-500 rounded-full" />
                  </div>
                  <p className="text-[10px] text-[#525252] font-ibm mt-2">Sosyal medya içeriği üretiliyor...</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════ SOCIAL PROOF ═══════════════════════ */

function SocialProof({ content }) {
  const c = content?.social_proof || {};
  const logos = ['Agency Pro', 'MediaFlow', 'BrandKit', 'SocialHub', 'CreatorX'];
  const testimonials = [
    { name: c.testimonial_1_name || 'Ayşe K.', role: c.testimonial_1_role || 'Dijital Ajans Sahibi', text: c.testimonial_1_text || 'Sosyal.Team ile haftalık içerik üretim süremiz 15 saatten 2 saate düştü. Müşterilerimiz farkı hemen fark etti.' },
    { name: c.testimonial_2_name || 'Mehmet T.', role: c.testimonial_2_role || 'E-ticaret Yöneticisi', text: c.testimonial_2_text || 'Artık 5 farklı marka için tek panelden içerik yönetiyorum. Otomasyon özelliği gerçekten oyun değiştirici.' },
    { name: c.testimonial_3_name || 'Elif S.', role: c.testimonial_3_role || 'Sosyal Medya Uzmanı', text: c.testimonial_3_text || 'AI görsel üretimi inanılmaz. Canva yerine Sosyal.Team kullanmaya başladım ve geri dönüş yok.' },
  ];
  return (
    <Section className="py-16 border-y border-white/5 bg-[#080808]">
      <div className="max-w-7xl mx-auto text-center">
        <motion.p variants={fadeUp} className="text-sm text-[#525252] font-ibm mb-8">{c.tagline || "1.000'den fazla içerik üreticisi ve ajans tarafından tercih ediliyor"}</motion.p>
        <motion.div variants={fadeUp} className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-40">
          {logos.map(l => (
            <div key={l} className="flex items-center gap-2 text-[#525252]">
              <Globe size={18} />
              <span className="font-outfit font-semibold text-sm">{l}</span>
            </div>
          ))}
        </motion.div>

        <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
          {testimonials.map(t => (
            <div key={t.name} className="p-6 rounded-xl bg-[#0F0F0F] border border-white/5 text-left hover:border-violet-500/20 transition-all" data-testid={`testimonial-${t.name.replace(/\s/g, '-')}`}>
              <div className="flex gap-1 mb-3">{[...Array(5)].map((_, i) => <Star key={i} size={14} className="text-yellow-400 fill-yellow-400" />)}</div>
              <p className="text-sm text-[#A3A3A3] font-ibm leading-relaxed mb-4">{`"${t.text}"`}</p>
              <div>
                <p className="text-sm text-white font-ibm font-medium">{t.name}</p>
                <p className="text-xs text-[#525252] font-ibm">{t.role}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </Section>
  );
}

/* ═══════════════════════ PROBLEM ═══════════════════════ */

function Problem() {
  const problems = [
    { icon: Clock, title: 'İçerik üretmek çok uzun sürüyor', desc: 'Tek bir sosyal medya paylaşımı için saatler harcamanıza gerek yok.' },
    { icon: Layers, title: 'Birden fazla platform yönetmek zor', desc: 'Her platform için ayrı içerik, ayrı format, ayrı zamanlama...' },
    { icon: Users, title: 'İçerik ekibi kurmak pahalı', desc: 'Tasarımcı, yazar, video editörü... hepsi için ayrı bütçe.' },
    { icon: Target, title: 'Tutarsız marka kimliği', desc: 'Farklı kişilerin ürettiği içerikler birbirinden kopuk.' },
  ];

  return (
    <Section className="py-24" id="problem">
      <div className="max-w-7xl mx-auto">
        <motion.div variants={fadeUp} className="text-center mb-16">
          <span className="text-xs font-ibm text-red-400/80 bg-red-500/5 border border-red-500/10 px-3 py-1 rounded-full">Problem</span>
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mt-6">
            {'İçerik üretimi neden bu kadar '}<span className="text-red-400">zor?</span>
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {problems.map((p, i) => (
            <motion.div key={i} variants={fadeUp}
              className="group flex gap-4 p-6 rounded-xl bg-[#0F0F0F] border border-white/5 hover:border-red-500/10 transition-all">
              <div className="shrink-0 w-10 h-10 rounded-lg bg-red-500/5 flex items-center justify-center group-hover:bg-red-500/10 transition-colors">
                <p.icon size={20} className="text-red-400/60" />
              </div>
              <div>
                <h3 className="text-white font-ibm font-medium text-sm">{p.title}</h3>
                <p className="text-[#737373] font-ibm text-xs mt-1 leading-relaxed">{p.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ═══════════════════════ SOLUTION ═══════════════════════ */

function Solution() {
  const solutions = [
    { icon: Brain, title: 'Tek platform, sınırsız içerik', desc: 'Metin, görsel, video ve ses — hepsi tek bir yerden.' },
    { icon: Sparkles, title: 'AI tam içerik paketleri üretir', desc: 'Bir tıkla başlık, görsel, açıklama ve hashtagler hazır.' },
    { icon: Send, title: 'Otomatik sosyal medya yayını', desc: 'X, Instagram, Telegram ve daha fazlasına doğrudan gönderin.' },
    { icon: Palette, title: 'Çoklu marka desteği', desc: 'Her marka için ayrı ses tonu, renk paleti ve şablon.' },
  ];

  return (
    <Section className="py-24">
      <div className="max-w-7xl mx-auto">
        <motion.div variants={fadeUp} className="text-center mb-16">
          <span className="text-xs font-ibm text-green-400/80 bg-green-500/5 border border-green-500/10 px-3 py-1 rounded-full">Çözüm</span>
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mt-6">
            {'Sosyal.Team ile her şey '}<span className="bg-gradient-to-r from-green-400 to-cyan-400 bg-clip-text text-transparent">basit</span>
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {solutions.map((s, i) => (
            <motion.div key={i} variants={fadeUp}
              className="group flex gap-4 p-6 rounded-xl bg-[#0F0F0F] border border-white/5 hover:border-green-500/15 transition-all">
              <div className="shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-green-500/10 to-cyan-500/10 flex items-center justify-center group-hover:from-green-500/20 group-hover:to-cyan-500/20 transition-colors">
                <s.icon size={20} className="text-green-400" />
              </div>
              <div>
                <h3 className="text-white font-ibm font-medium text-sm">{s.title}</h3>
                <p className="text-[#737373] font-ibm text-xs mt-1 leading-relaxed">{s.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ═══════════════════════ FEATURES ═══════════════════════ */

function Features() {
  const features = [
    { icon: Brain, title: 'AI Metin Üretimi', desc: 'Blog, sosyal medya, reklam metinleri — saniyeler içinde.', color: 'violet' },
    { icon: Image, title: 'AI Görsel Oluşturma', desc: 'Profesyonel görseller ve bannerlar tek tıkla.', color: 'blue' },
    { icon: Video, title: 'AI Video Üretimi', desc: 'Kısa videolar ve Reels içerikleri otomatik.', color: 'cyan' },
    { icon: Mic, title: 'Ses ve Altyazı', desc: 'Metinden sese dönüştürme ve otomatik altyazı.', color: 'pink' },
    { icon: Send, title: 'Sosyal Medya Otomasyonu', desc: 'X, Instagram, Telegrama otomatik paylaşım.', color: 'green' },
    { icon: Users, title: 'Çoklu Bayi Paneli', desc: 'Her müşteri için izole alan ve yetki.', color: 'orange' },
    { icon: Calendar, title: 'İçerik Zamanlayıcı', desc: 'İleriye dönük gönderi planlama ve takvim.', color: 'yellow' },
    { icon: BarChart3, title: 'Analiz ve Performans', desc: 'Erişim, etkileşim ve dönüşüm metrikleri.', color: 'red' },
  ];

  const colorMap = {
    violet: 'from-violet-500/10 to-violet-600/5 text-violet-400 group-hover:from-violet-500/20',
    blue: 'from-blue-500/10 to-blue-600/5 text-blue-400 group-hover:from-blue-500/20',
    cyan: 'from-cyan-500/10 to-cyan-600/5 text-cyan-400 group-hover:from-cyan-500/20',
    pink: 'from-pink-500/10 to-pink-600/5 text-pink-400 group-hover:from-pink-500/20',
    green: 'from-green-500/10 to-green-600/5 text-green-400 group-hover:from-green-500/20',
    orange: 'from-orange-500/10 to-orange-600/5 text-orange-400 group-hover:from-orange-500/20',
    yellow: 'from-yellow-500/10 to-yellow-600/5 text-yellow-400 group-hover:from-yellow-500/20',
    red: 'from-red-500/10 to-red-600/5 text-red-400 group-hover:from-red-500/20',
  };

  return (
    <Section className="py-24" id="features">
      <div className="max-w-7xl mx-auto">
        <motion.div variants={fadeUp} className="text-center mb-16">
          <span className="text-xs font-ibm text-violet-400/80 bg-violet-500/5 border border-violet-500/10 px-3 py-1 rounded-full">Özellikler</span>
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mt-6">
            {'İçerik üretiminin '}<span className="bg-gradient-to-r from-violet-400 to-blue-400 bg-clip-text text-transparent">tüm araçları</span>
          </h2>
          <p className="mt-3 text-[#737373] font-ibm text-sm max-w-lg mx-auto">Tek bir platformda ihtiyacınız olan her şey</p>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((f, i) => (
            <motion.div key={i} variants={scaleIn}
              className="group p-5 rounded-xl bg-[#0F0F0F] border border-white/5 hover:border-white/10 transition-all hover:-translate-y-1" data-testid={`feature-card-${i}`}>
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colorMap[f.color]} flex items-center justify-center transition-colors mb-4`}>
                <f.icon size={20} />
              </div>
              <h3 className="text-white font-ibm font-medium text-sm">{f.title}</h3>
              <p className="text-[#525252] font-ibm text-xs mt-1.5 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ═══════════════════════ HOW IT WORKS ═══════════════════════ */

function HowItWorks() {
  const steps = [
    { num: '01', title: 'Hesaplarınızı bağlayın', desc: 'X, Instagram, Telegram ve diğer platformları tek tıkla entegre edin.', icon: Globe },
    { num: '02', title: 'İçerik tipini seçin', desc: 'Metin, görsel, video veya tam paket — ne istiyorsanız.', icon: Layers },
    { num: '03', title: 'AI ile üretin', desc: 'Yapay zeka saniyeler içinde profesyonel içerik üretsin.', icon: Sparkles },
    { num: '04', title: 'Otomatik yayınlayın', desc: 'Hemen paylaşın veya ileri tarihe zamanlasın.', icon: Send },
  ];

  return (
    <Section className="py-24" id="how">
      <div className="max-w-7xl mx-auto">
        <motion.div variants={fadeUp} className="text-center mb-16">
          <span className="text-xs font-ibm text-cyan-400/80 bg-cyan-500/5 border border-cyan-500/10 px-3 py-1 rounded-full">Nasıl Çalışır</span>
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mt-6">
            {'4 adımda '}<span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">içerik üretin</span>
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <motion.div key={i} variants={fadeUp} className="relative text-center" data-testid={`step-${s.num}`}>
              {i < 3 && <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-px bg-gradient-to-r from-white/10 to-transparent" />}
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-white/5 flex items-center justify-center mx-auto mb-4">
                <s.icon size={24} className="text-cyan-400" />
              </div>
              <span className="text-xs font-jetbrains text-cyan-400/60">{s.num}</span>
              <h3 className="text-white font-ibm font-medium text-sm mt-1">{s.title}</h3>
              <p className="text-[#525252] font-ibm text-xs mt-1.5 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ═══════════════════════ PRICING ═══════════════════════ */

function Pricing({ content }) {
  const navigate = useNavigate();
  const c = content?.pricing || {};
  const plans = [
    {
      name: c.plan1_name || 'Starter', price: c.plan1_price || '299', period: '/ay', desc: c.plan1_desc || 'Bireysel içerik üreticileri için',
      features: c.plan1_features ? c.plan1_features.split(',').map(f => f.trim()) : ['500 AI kredi / ay', 'Metin ve görsel üretimi', '2 sosyal medya hesabı', 'Temel analizler', 'E-posta desteği'],
      cta: 'Başla', highlighted: false,
    },
    {
      name: c.plan2_name || 'Pro', price: c.plan2_price || '799', period: '/ay', desc: c.plan2_desc || 'Büyüyen markalar ve ekipler için',
      features: c.plan2_features ? c.plan2_features.split(',').map(f => f.trim()) : ['3.000 AI kredi / ay', 'Tüm içerik tipleri', '10 sosyal medya hesabı', 'Gelişmiş analizler', 'Öncelikli destek', 'Otomatik yayın', 'İçerik takvimi'],
      cta: "Pro'yu Seç", highlighted: true,
    },
    {
      name: c.plan3_name || 'Elite', price: c.plan3_price || '1.999', period: '/ay', desc: c.plan3_desc || 'Ajanslar ve enterprise için',
      features: c.plan3_features ? c.plan3_features.split(',').map(f => f.trim()) : ['Sınırsız AI kredi', 'Tüm premium özellikler', 'Sınırsız hesap', 'Beyaz etiket', 'Özel API erişimi', '7/24 öncelikli destek', 'Özel eğitim'],
      cta: 'İletişime Geç', highlighted: false,
    },
  ];

  return (
    <Section className="py-24" id="pricing">
      <div className="max-w-7xl mx-auto">
        <motion.div variants={fadeUp} className="text-center mb-16">
          <span className="text-xs font-ibm text-violet-400/80 bg-violet-500/5 border border-violet-500/10 px-3 py-1 rounded-full">Fiyatlandırma</span>
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mt-6">
            {'Her bütçeye uygun '}<span className="bg-gradient-to-r from-violet-400 to-blue-400 bg-clip-text text-transparent">planlar</span>
          </h2>
          <p className="mt-3 text-[#737373] font-ibm text-sm">14 gün ücretsiz deneme. Kredi kartı gerekmez.</p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((p, i) => (
            <motion.div key={i} variants={scaleIn}
              data-testid={`pricing-card-${p.name.toLowerCase()}`}
              className={`relative p-6 rounded-2xl border transition-all ${
                p.highlighted
                  ? 'bg-gradient-to-b from-violet-500/5 to-[#0F0F0F] border-violet-500/30 shadow-xl shadow-violet-500/5'
                  : 'bg-[#0F0F0F] border-white/5 hover:border-white/10'
              }`}>
              {p.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="text-[10px] font-ibm font-medium px-3 py-1 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 text-white">En Popüler</span>
                </div>
              )}
              <h3 className="font-outfit text-lg font-bold text-white">{p.name}</h3>
              <p className="text-xs text-[#525252] font-ibm mt-1">{p.desc}</p>
              <div className="mt-4 mb-6">
                <span className="font-outfit text-4xl font-bold text-white">{'\u0024'}{p.price}</span>
                <span className="text-sm text-[#525252] font-ibm">{p.period}</span>
              </div>
              <button onClick={() => navigate('/login')}
                data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                className={`w-full py-2.5 rounded-full text-sm font-ibm font-medium transition-all ${
                  p.highlighted
                    ? 'bg-gradient-to-r from-violet-600 to-blue-600 text-white hover:from-violet-500 hover:to-blue-500 shadow-lg shadow-violet-500/20'
                    : 'border border-white/10 text-white hover:bg-white/5'
                }`}>
                {p.cta}
              </button>
              <div className="mt-6 space-y-2.5">
                {p.features.map(f => (
                  <div key={f} className="flex items-center gap-2">
                    <Check size={14} className={p.highlighted ? 'text-violet-400' : 'text-[#525252]'} />
                    <span className="text-xs text-[#A3A3A3] font-ibm">{f}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ═══════════════════════ USE CASES ═══════════════════════ */

function UseCases() {
  const cases = [
    { icon: TrendingUp, title: 'Dijital Ajanslar', desc: 'Müşteri portföyünüz için ölçekli içerik üretimi.' },
    { icon: Award, title: 'E-ticaret Markaları', desc: 'Ürün tanıtımı ve kampanya içerikleri otomatik.' },
    { icon: Star, title: 'Influencerlar', desc: 'Kişisel marka içerik üretimi ve zamanlama.' },
    { icon: Zap, title: 'SaaS Şirketleri', desc: 'Ürün pazarlama içerikleri ve sosyal medya.' },
  ];

  return (
    <Section className="py-24">
      <div className="max-w-7xl mx-auto">
        <motion.div variants={fadeUp} className="text-center mb-16">
          <span className="text-xs font-ibm text-blue-400/80 bg-blue-500/5 border border-blue-500/10 px-3 py-1 rounded-full">Kullanım Alanları</span>
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mt-6">Her sektör için</h2>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {cases.map((c, i) => (
            <motion.div key={i} variants={fadeUp}
              className="group p-6 rounded-xl bg-[#0F0F0F] border border-white/5 hover:border-blue-500/15 transition-all text-center">
              <div className="w-12 h-12 rounded-xl bg-blue-500/5 flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-500/10 transition-colors">
                <c.icon size={22} className="text-blue-400" />
              </div>
              <h3 className="text-white font-ibm font-medium text-sm">{c.title}</h3>
              <p className="text-[#525252] font-ibm text-xs mt-1.5 leading-relaxed">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ═══════════════════════ CTA ═══════════════════════ */

function CtaSection() {
  const navigate = useNavigate();
  return (
    <Section className="py-24">
      <div className="max-w-4xl mx-auto">
        <motion.div variants={fadeUp}
          className="relative p-12 md:p-16 rounded-3xl overflow-hidden text-center">
          <div className="absolute inset-0 bg-gradient-to-br from-violet-600/20 via-blue-600/10 to-cyan-600/20 rounded-3xl" />
          <div className="absolute inset-0 border border-white/5 rounded-3xl" />
          <div className="absolute top-0 left-1/3 w-[300px] h-[300px] bg-violet-500/10 rounded-full blur-[80px]" />
          <div className="relative">
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white">
              {'İçeriğinizi bugün '}<span className="bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">otomatikleştirin</span>
            </h2>
            <p className="mt-4 text-[#A3A3A3] font-ibm text-sm max-w-lg mx-auto">
              14 gün ücretsiz deneyin. Kredi kartı gerekmez. Dakikalar içinde başlayın.
            </p>
            <button onClick={() => navigate('/login')} data-testid="cta-start-btn"
              className="mt-8 inline-flex items-center gap-2 px-10 py-4 rounded-full bg-white text-black font-ibm font-semibold text-sm hover:bg-[#E5E5E5] transition-all shadow-2xl shadow-white/10 hover:scale-105">
              Ücretsiz Başla <ArrowRight size={16} />
            </button>
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

/* ═══════════════════════ FAQ ═══════════════════════ */

function Faq({ content }) {
  const [open, setOpen] = useState(null);
  const c = content?.faq || {};
  const items = [
    { q: c.faq_1_q || 'Sosyal.Team nedir?', a: c.faq_1_a || 'Sosyal.Team, yapay zeka destekli bir içerik yönetim platformudur. Metin, görsel, video ve ses içerikleri tek bir panelden üretip sosyal medya hesaplarınıza otomatik olarak yayınlayabilirsiniz.' },
    { q: c.faq_2_q || 'Tasarım becerisi gerekiyor mu?', a: c.faq_2_a || 'Hayır! AI sizin için profesyonel görseller ve videolar üretir. Herhangi bir tasarım aracı bilmenize gerek yok.' },
    { q: c.faq_3_q || 'Kendi sosyal medya hesaplarımı bağlayabilir miyim?', a: c.faq_3_a || 'Evet! X (Twitter), Instagram, Telegram ve daha fazla platforma kendi hesabınızla bağlanabilir, doğrudan içerik yayınlayabilirsiniz.' },
    { q: c.faq_4_q || 'Fiyatlandırma nasıl çalışıyor?', a: c.faq_4_a || 'Kredi tabanlı bir sistem kullanıyoruz. Her plan belirli sayıda AI kredi içerir. Kredileriniz bittiğinde ek kredi satın alabilir veya planınızı yükseltebilirsiniz.' },
    { q: c.faq_5_q || 'Ücretsiz deneme var mı?', a: c.faq_5_a || 'Evet! 14 gün boyunca tüm özellikleri ücretsiz deneyebilirsiniz. Kredi kartı bilgisi gerekmez.' },
  ];

  return (
    <Section className="py-24" id="faq">
      <div className="max-w-3xl mx-auto">
        <motion.div variants={fadeUp} className="text-center mb-16">
          <span className="text-xs font-ibm text-violet-400/80 bg-violet-500/5 border border-violet-500/10 px-3 py-1 rounded-full">SSS</span>
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mt-6">Sık sorulan sorular</h2>
        </motion.div>
        <motion.div variants={fadeUp} className="space-y-3">
          {items.map((item, i) => (
            <div key={i} className="rounded-xl border border-white/5 bg-[#0F0F0F] overflow-hidden" data-testid={`faq-item-${i}`}>
              <button onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between p-5 text-left" data-testid={`faq-toggle-${i}`}>
                <span className="text-sm text-white font-ibm font-medium">{item.q}</span>
                <ChevronDown size={16} className={`text-[#525252] transition-transform ${open === i ? 'rotate-180' : ''}`} />
              </button>
              {open === i && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} transition={{ duration: 0.3 }}
                  className="px-5 pb-5">
                  <p className="text-xs text-[#A3A3A3] font-ibm leading-relaxed">{item.a}</p>
                </motion.div>
              )}
            </div>
          ))}
        </motion.div>
      </div>
    </Section>
  );
}

/* ═══════════════════════ FOOTER ═══════════════════════ */

function Footer() {
  const footerLinks = [
    { title: 'Ürün', items: [
      { label: 'Özellikler', href: '/#features' },
      { label: 'Fiyatlandırma', href: '/#pricing' },
      { label: 'SSS', href: '/#faq' },
    ]},
    { title: 'Şirket', items: [
      { label: 'Hakkımızda', href: '/sayfa/about' },
      { label: 'İletişim', href: '/sayfa/about' },
    ]},
    { title: 'Yasal', items: [
      { label: 'Gizlilik Politikası', href: '/sayfa/privacy' },
      { label: 'Kullanım Koşulları', href: '/sayfa/terms' },
      { label: 'Çerez Politikası', href: '/sayfa/cookies' },
    ]},
  ];

  return (
    <footer className="border-t border-white/5 bg-[#080808]" data-testid="footer-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center">
                <Sparkles size={16} className="text-white" />
              </div>
              <span className="font-outfit text-lg font-bold text-white">Sosyal<span className="text-violet-400">.Team</span></span>
            </div>
            <p className="text-xs text-[#525252] font-ibm leading-relaxed">AI destekli içerik üretim ve dağıtım platformu.</p>
          </div>
          {footerLinks.map(group => (
            <div key={group.title}>
              <h4 className="text-xs text-white font-ibm font-medium mb-4">{group.title}</h4>
              <ul className="space-y-2.5">
                {group.items.map(item => (
                  <li key={item.label}><Link to={item.href} className="text-xs text-[#525252] hover:text-white font-ibm transition-colors">{item.label}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-12 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-[#525252] font-ibm">&copy; 2026 Sosyal.Team. Tüm hakları saklıdır.</p>
          <div className="flex gap-4">
            {['X', 'in', 'ig'].map(s => (
              <a key={s} href="#" className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-[#525252] hover:text-white hover:bg-white/10 transition-all">
                <span className="text-xs font-bold">{s}</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ═══════════════════════ MAIN PAGE ═══════════════════════ */

export default function LandingPage() {
  const [content, setContent] = useState({});

  useEffect(() => {
    fetch(`${API}/api/site-content`)
      .then(r => r.json())
      .then(data => setContent(data))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden" data-testid="landing-page">
      <Nav />
      <Hero content={content} />
      <SocialProof content={content} />
      <Problem />
      <Solution />
      <Features />
      <HowItWorks />
      <Pricing content={content} />
      <UseCases />
      <CtaSection />
      <Faq content={content} />
      <Footer />
    </div>
  );
}
